import { type TenantResponseDto, TenantsApi } from '@saaga/billingv2-sdk'
import { setTenant } from '@saaga/shared/store/slices/billing.slice'
import { useAppDispatch } from '@saaga/shared/store/store'
import { transformTenantResponse } from '@saaga/shared/utils/billing.util'
import { useCallback } from 'react'

import { type BillingUserInfo, useBillingConfig } from './use-billing-config.hook'

export interface UseTenantApiReturn {
  getTenantClient: () => Promise<TenantsApi>
  syncTenant: () => Promise<TenantResponseDto>
  getCurrentTenant: () => Promise<TenantResponseDto>
  refetchTenant: () => Promise<void>
}

export const useTenantApi = (): UseTenantApiReturn => {
  const { createConfig, userInfo, orgId } = useBillingConfig()
  const dispatch = useAppDispatch()

  const getTenantClient = useCallback(async () => {
    const config = await createConfig(false)
    return new TenantsApi(config)
  }, [createConfig])

  const syncTenant = useCallback(async (): Promise<TenantResponseDto> => {
    if (!orgId) {
      throw new Error('No organization selected')
    }

    const client = await getTenantClient()
    return client.tenantControllerCreateTenant({
      xTenantId: orgId,
      createTenantDto: buildTenantDto(userInfo),
    })
  }, [getTenantClient, orgId, userInfo])

  const getCurrentTenant = useCallback(async (): Promise<TenantResponseDto> => {
    if (!orgId) {
      throw new Error('No organization selected')
    }

    const client = await getTenantClient()
    return client.tenantDashboardControllerGetCurrentTenant({ xTenantId: orgId })
  }, [getTenantClient, orgId])

  const refetchTenant = useCallback(async (): Promise<void> => {
    try {
      const tenantResponse = await getCurrentTenant()
      const tenant = transformTenantResponse(tenantResponse)
      dispatch(setTenant(tenant))
    } catch {
      // Silently fail - the UI will show stale data but won't break
    }
  }, [getCurrentTenant, dispatch])

  return { getTenantClient, syncTenant, getCurrentTenant, refetchTenant }
}

const buildTenantDto = (userInfo: BillingUserInfo) => ({
  name: userInfo.name,
  email: userInfo.email,
  address: {
    line1: 'N/A',
    city: 'N/A',
    postalCode: '00000',
    country: 'US',
  },
})
